import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  FileText,
  CheckCircle,
  XCircle,
  HelpCircle,
  Download,
  Target,
  Landmark,
  Loader2,
  ChevronDown,
  User,
  TrendingUp,
  Calendar,
  BarChart3,
  Award,
  X,
  Edit,
  Eye
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Helper function to format dates as mm/dd/yy
const formatDateMMDDYY = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  } catch {
    return dateStr;
  }
};

// Essential Functions and LA Competencies for staff evaluation
const ESSENTIAL_FUNCTIONS = [
  { value: "Patron Assistance (35%)", label: "Patron Assistance (35%)" },
  { value: "Circulation (20%)", label: "Circulation (20%)" },
  { value: "Technology Instruction (15%)", label: "Technology Instruction (15%)" },
  { value: "Programming (15%)", label: "Programming (15%)" },
  { value: "Collection Maintenance (10%)", label: "Collection Maintenance (10%)" },
  { value: "Other Duties (5%)", label: "Other Duties (5%)" }
];

const LA_COMPETENCIES = [
  { value: "Focuses on the Customer", label: "Focuses on the Customer" },
  { value: "Takes Initiative", label: "Takes Initiative" },
  { value: "Contributes to a Positive Work Environment", label: "Contributes to a Positive Work Environment" },
  { value: "Strategic and Systems Thinking", label: "Strategic and Systems Thinking" },
  { value: "Pursues Personal Development", label: "Pursues Personal Development" },
  { value: "Continuous Improvement", label: "Continuous Improvement" },
  { value: "Takes Pride in the Workplace", label: "Takes Pride in the Workplace" }
];

export const SupervisorDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Dialog states
  const [approveDialog, setApproveDialog] = useState({ open: false, proposal: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, proposal: null });
  const [returnDialog, setReturnDialog] = useState({ open: false, proposal: null });
  
  // Form states
  const [approvedDate, setApprovedDate] = useState("");
  const [approvedTime, setApprovedTime] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [returnQuestions, setReturnQuestions] = useState("");
  
  // Export state
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportLoading, setExportLoading] = useState(false);
  
  // Staff Performance Report state
  const [performanceDialogOpen, setPerformanceDialogOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("1");
  const [performanceData, setPerformanceData] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  
  // Pending competency suggestions
  const [pendingCompetencies, setPendingCompetencies] = useState([]);
  const [competencyActionLoading, setCompetencyActionLoading] = useState(null);
  
  // Competency review dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewingEvent, setReviewingEvent] = useState(null);
  const [editedCompetencies, setEditedCompetencies] = useState([]);
  const [rejectNotes, setRejectNotes] = useState("");

  useEffect(() => {
    fetchDashboardData();
    fetchPendingCompetencies();
    fetchAllUsers();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/supervisor/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching supervisor dashboard:", error);
      toast.error("Failed to load supervisor dashboard");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/supervisor/users`);
      setAllUsers(response.data);
      if (response.data.length > 0) {
        setSelectedStaffId(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };
  
  const fetchPendingCompetencies = async () => {
    try {
      const response = await axios.get(`${API}/competency-suggestions/pending`);
      setPendingCompetencies(response.data);
    } catch (error) {
      console.error("Error fetching pending competencies:", error);
    }
  };
  
  const generatePerformanceReport = async () => {
    if (!selectedStaffId) return;
    setPerformanceLoading(true);
    try {
      const response = await axios.get(`${API}/supervisor/staff-performance/${selectedStaffId}?period=${selectedPeriod}`);
      setPerformanceData(response.data);
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Failed to generate performance report");
    } finally {
      setPerformanceLoading(false);
    }
  };
  
  // Open the review dialog for an event
  const openReviewDialog = (event) => {
    setReviewingEvent(event);
    setEditedCompetencies(event.suggested_competencies || []);
    setRejectNotes("");
    setReviewDialogOpen(true);
  };
  
  // Toggle a competency in the edit list
  const toggleCompetency = (comp) => {
    if (editedCompetencies.includes(comp)) {
      setEditedCompetencies(editedCompetencies.filter(c => c !== comp));
    } else {
      setEditedCompetencies([...editedCompetencies, comp]);
    }
  };
  
  const handleCompetencyApprove = async () => {
    if (!reviewingEvent) return;
    setCompetencyActionLoading(reviewingEvent.id);
    try {
      await axios.post(`${API}/events/${reviewingEvent.id}/competencies/approve`, {
        competencies: editedCompetencies
      });
      toast.success("Competencies approved");
      setReviewDialogOpen(false);
      setReviewingEvent(null);
      fetchPendingCompetencies();
    } catch (error) {
      toast.error("Failed to approve competencies");
    } finally {
      setCompetencyActionLoading(null);
    }
  };
  
  const handleCompetencyReject = async () => {
    if (!reviewingEvent) return;
    setCompetencyActionLoading(reviewingEvent.id);
    try {
      await axios.post(`${API}/events/${reviewingEvent.id}/competencies/reject`, null, {
        params: { reason: rejectNotes }
      });
      toast.success("Competencies rejected");
      setReviewDialogOpen(false);
      setReviewingEvent(null);
      fetchPendingCompetencies();
    } catch (error) {
      toast.error("Failed to reject competencies");
    } finally {
      setCompetencyActionLoading(null);
    }
  };

  // Report export loading state
  const [reportExportLoading, setReportExportLoading] = useState(null);

  // Download Word report
  const handleExportDocx = async () => {
    if (!selectedStaffId) return;
    setReportExportLoading('docx');
    try {
      const response = await axios.get(
        `${API}/supervisor/staff-report/${selectedStaffId}/docx?period=${selectedPeriod}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from selected user
      const selectedUser = allUsers.find(u => u.id === selectedStaffId);
      const userName = selectedUser?.name || 'Staff';
      const monthYear = formatDateMMDDYY(new Date()).replace(/\//g, '_');
      link.download = `Staff_Report_${userName.replace(/\s+/g, '_')}_${monthYear}.docx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Word report downloaded");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to download Word report");
    } finally {
      setReportExportLoading(null);
    }
  };

  // Download PDF report
  const handleExportPdf = async () => {
    if (!selectedStaffId) return;
    setReportExportLoading('pdf');
    try {
      const response = await axios.get(
        `${API}/supervisor/staff-report/${selectedStaffId}/pdf?period=${selectedPeriod}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from selected user
      const selectedUser = allUsers.find(u => u.id === selectedStaffId);
      const userName = selectedUser?.name || 'Staff';
      const monthYear = formatDateMMDDYY(new Date()).replace(/\//g, '_');
      link.download = `Staff_Report_${userName.replace(/\s+/g, '_')}_${monthYear}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF report downloaded");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to download PDF report");
    } finally {
      setReportExportLoading(null);
    }
  };

  const handleApprove = async () => {
    if (!approveDialog.proposal) return;
    setActionLoading(approveDialog.proposal.id);
    try {
      let url = `${API}/supervisor/proposals/${approveDialog.proposal.id}/approve`;
      const params = new URLSearchParams();
      if (approvedDate) params.append("approved_date", approvedDate);
      if (approvedTime) params.append("approved_time", approvedTime);
      if (params.toString()) url += `?${params.toString()}`;
      
      await axios.post(url);
      toast.success("Proposal approved successfully!");
      setApproveDialog({ open: false, proposal: null });
      setApprovedDate("");
      setApprovedTime("");
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to approve proposal");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.proposal) return;
    setActionLoading(rejectDialog.proposal.id);
    try {
      await axios.post(`${API}/supervisor/proposals/${rejectDialog.proposal.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success("Proposal rejected");
      setRejectDialog({ open: false, proposal: null });
      setRejectReason("");
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to reject proposal");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReturn = async () => {
    if (!returnDialog.proposal) return;
    setActionLoading(returnDialog.proposal.id);
    try {
      await axios.post(`${API}/supervisor/proposals/${returnDialog.proposal.id}/return?questions=${encodeURIComponent(returnQuestions)}`);
      toast.success("Proposal returned for more information");
      setReturnDialog({ open: false, proposal: null });
      setReturnQuestions("");
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to return proposal");
    } finally {
      setActionLoading(null);
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/supervisor/submissions/export?month=${exportMonth}&year=${exportYear}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const monthName = new Date(exportYear, exportMonth - 1).toLocaleString('default', { month: 'long' });
      link.setAttribute('download', `submissions_${monthName}_${exportYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Monthly submissions exported!");
    } catch (error) {
      toast.error("Failed to export submissions");
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { submitted_proposals = [], staff_stats = [] } = dashboardData || {};

  return (
    <div className="space-y-8" data-testid="supervisor-dashboard">
      {/* Header */}
      <div className="animate-fade-in flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
            Supervisor Dashboard
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage submissions, track staff progress, and monitor goals
          </p>
        </div>
        <Button
          onClick={() => setPerformanceDialogOpen(true)}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="generate-report-btn"
        >
          <BarChart3 className="h-4 w-4 mr-2" />
          Generate Report
        </Button>
      </div>

      {/* Pending Competency Suggestions Card */}
      {pendingCompetencies.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/50 border-l-4 border-l-amber-500" data-testid="pending-competencies">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-600" />
              <CardTitle className="font-heading text-xl text-amber-800">
                Pending Competency Suggestions ({pendingCompetencies.length})
              </CardTitle>
            </div>
            <CardDescription className="text-amber-700">
              Staff members have suggested competencies for their entries. Review and approve/reject below.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingCompetencies.map((event) => (
              <div
                key={event.id}
                className="flex items-center justify-between p-4 bg-white rounded-lg border border-amber-200"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-medium">{event.title}</span>
                    <Badge variant="outline" className="capitalize">
                      {event.event_type}
                    </Badge>
                    <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                      <User className="h-3 w-3 mr-1" />
                      {event.creator_name}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {event.suggested_competencies?.map((comp) => (
                      <Badge key={comp} className="bg-blue-100 text-blue-700">
                        {comp}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Button
                  onClick={() => openReviewDialog(event)}
                  className="bg-amber-500 hover:bg-amber-600 ml-4"
                  data-testid={`review-competency-${event.id}`}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Review
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Submitted Proposals Section */}
      <Card className="border-stone-200 border-l-4 border-l-amber-500" data-testid="submitted-proposals">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-amber-500" />
              <CardTitle className="font-heading text-xl">Pending Proposals</CardTitle>
              <Badge variant="secondary">{submitted_proposals.length}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(exportMonth)} onValueChange={(v) => setExportMonth(parseInt(v))}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>
                      {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={String(exportYear)} onValueChange={(v) => setExportYear(parseInt(v))}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map((year) => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleExport} disabled={exportLoading} variant="outline">
                {exportLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                Export
              </Button>
            </div>
          </div>
          <CardDescription>Proposals awaiting your review</CardDescription>
        </CardHeader>
        <CardContent>
          {submitted_proposals.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p>No pending proposals to review</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Program Name</TableHead>
                  <TableHead>Proposed Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submitted_proposals.map((proposal) => (
                  <TableRow key={proposal.id}>
                    <TableCell className="font-medium">{proposal.program_name}</TableCell>
                    <TableCell>{proposal.proposed_date || "TBD"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {proposal.program_type?.replace("_", " ") || "N/A"}
                      </Badge>
                    </TableCell>
                    <TableCell>${proposal.cost_estimate || 0}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            setApprovedDate(proposal.proposed_date || "");
                            setApprovedTime(proposal.proposed_time || "");
                            setApproveDialog({ open: true, proposal });
                          }}
                          disabled={actionLoading === proposal.id}
                          data-testid={`approve-${proposal.id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-amber-600 border-amber-600 hover:bg-amber-50"
                          onClick={() => setReturnDialog({ open: true, proposal })}
                          disabled={actionLoading === proposal.id}
                          data-testid={`return-${proposal.id}`}
                        >
                          <HelpCircle className="h-4 w-4 mr-1" />
                          Return
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-600 hover:bg-red-50"
                          onClick={() => setRejectDialog({ open: true, proposal })}
                          disabled={actionLoading === proposal.id}
                          data-testid={`reject-${proposal.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Staff Member Cards */}
      <div>
        <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
          <Users className="h-5 w-5" />
          Staff Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {staff_stats.map((staff) => (
            <Card key={staff.user_id} className="border-stone-200 hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-semibold">
                    {staff.name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <CardTitle className="text-base">{staff.name}</CardTitle>
                    <CardDescription className="text-xs">{staff.email}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Submitted Proposals */}
                <div className="flex items-center justify-between p-2 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-amber-600" />
                    <span className="text-sm">Programs Submitted</span>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700">
                    {staff.submitted_proposals_count}
                  </Badge>
                </div>
                
                {/* Built Stations */}
                <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Landmark className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Stations Built</span>
                  </div>
                  <Badge className="bg-green-100 text-green-700">
                    {staff.built_stations_count}
                  </Badge>
                </div>
                
                {/* Built Station Names */}
                {staff.built_stations?.length > 0 && (
                  <div className="pl-2 border-l-2 border-green-200">
                    <p className="text-xs text-muted-foreground mb-1">Built Stations:</p>
                    <div className="flex flex-wrap gap-1">
                      {staff.built_stations.slice(0, 3).map((station) => (
                        <Badge key={station.id} variant="outline" className="text-xs">
                          {station.name}
                        </Badge>
                      ))}
                      {staff.built_stations.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{staff.built_stations.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Primary Goals Progress */}
      <Card className="border-stone-200 border-l-4 border-l-purple-500" data-testid="primary-goals">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-purple-500" />
            <CardTitle className="font-heading text-xl">Staff Primary Goals Progress</CardTitle>
          </div>
          <CardDescription>Each staff member's primary goal and their progress</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {staff_stats.filter(s => s.primary_goal).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Target className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No staff members have set a primary goal yet</p>
              </div>
            ) : (
              staff_stats.filter(s => s.primary_goal).map((staff) => {
                const goal = staff.primary_goal;
                const progress = goal.target_value > 0 
                  ? Math.min(100, Math.round((goal.current_value / goal.target_value) * 100))
                  : 0;
                
                return (
                  <div key={staff.user_id} className="p-4 border rounded-lg bg-muted/30">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                          {staff.name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{staff.name}</p>
                          <p className="text-xs text-muted-foreground">{goal.title}</p>
                        </div>
                      </div>
                      <Badge className={
                        goal.status === "completed" ? "bg-green-100 text-green-700" :
                        goal.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                        "bg-gray-100 text-gray-700"
                      }>
                        {goal.status?.replace("_", " ")}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{goal.category}</span>
                        <span className="font-medium">{goal.current_value} / {goal.target_value} {goal.unit}</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                      <p className="text-xs text-right text-muted-foreground">{progress}% complete</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={approveDialog.open} onOpenChange={(open) => setApproveDialog({ open, proposal: approveDialog.proposal })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Proposal</DialogTitle>
            <DialogDescription>
              Approve "{approveDialog.proposal?.program_name}" and create an event.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Event Date</Label>
              <Input 
                type="date" 
                value={approvedDate} 
                onChange={(e) => setApprovedDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Event Time</Label>
              <Input 
                type="time" 
                value={approvedTime} 
                onChange={(e) => setApprovedTime(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog({ open: false, proposal: null })}>
              Cancel
            </Button>
            <Button onClick={handleApprove} className="bg-green-600 hover:bg-green-700">
              Approve & Create Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ open, proposal: rejectDialog.proposal })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Proposal</DialogTitle>
            <DialogDescription>
              Reject "{rejectDialog.proposal?.program_name}". Provide a reason (optional).
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason for Rejection</Label>
            <Textarea 
              value={rejectReason} 
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Optional: Explain why this proposal is being rejected..."
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, proposal: null })}>
              Cancel
            </Button>
            <Button onClick={handleReject} variant="destructive">
              Reject Proposal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={returnDialog.open} onOpenChange={(open) => setReturnDialog({ open, proposal: returnDialog.proposal })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return for More Information</DialogTitle>
            <DialogDescription>
              Return "{returnDialog.proposal?.program_name}" to the submitter with questions.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Questions / Information Needed</Label>
            <Textarea 
              value={returnQuestions} 
              onChange={(e) => setReturnQuestions(e.target.value)}
              placeholder="What additional information or clarification do you need?"
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnDialog({ open: false, proposal: null })}>
              Cancel
            </Button>
            <Button onClick={handleReturn} className="bg-amber-600 hover:bg-amber-700">
              Return for Info
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff Performance Report Dialog */}
      <Dialog open={performanceDialogOpen} onOpenChange={setPerformanceDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Staff Performance Report
            </DialogTitle>
            <DialogDescription>
              Select a staff member and time period to generate a comprehensive report
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>Staff Member</Label>
              <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {allUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Period</Label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Month</SelectItem>
                  <SelectItem value="3">3 Months</SelectItem>
                  <SelectItem value="6">6 Months</SelectItem>
                  <SelectItem value="12">12 Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-end mb-4">
            <Button onClick={generatePerformanceReport} disabled={performanceLoading || !selectedStaffId}>
              {performanceLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <BarChart3 className="h-4 w-4 mr-2" />
              )}
              Generate
            </Button>
          </div>
          
          {performanceData && (
            <div className="space-y-6 border-t pt-4">
              {/* Staff Info Header */}
              <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-lg">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-lg font-semibold">
                  {performanceData.user.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{performanceData.user.name}</h3>
                  <p className="text-sm text-muted-foreground">{performanceData.user.email}</p>
                  <p className="text-xs text-purple-600">
                    {formatDateMMDDYY(new Date())}
                  </p>
                </div>
              </div>
              
              {/* Year-to-Date Summary */}
              <div>
                <h4 className="flex items-center gap-2 text-sm font-medium text-purple-700 mb-3">
                  <TrendingUp className="h-4 w-4" />
                  Year-to-Date Summary
                </h4>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  <div className="p-3 bg-green-50 rounded-lg text-center border border-green-200">
                    <div className="text-2xl font-bold text-green-600">{performanceData.ytd_summary.completed_events}</div>
                    <div className="text-xs text-green-700">Completed Events</div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg text-center border border-blue-200">
                    <div className="text-2xl font-bold text-blue-600">{performanceData.ytd_summary.in_progress}</div>
                    <div className="text-xs text-blue-700">In Progress</div>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg text-center border border-amber-200">
                    <div className="text-2xl font-bold text-amber-600">{performanceData.ytd_summary.proposals_entered}</div>
                    <div className="text-xs text-amber-700">Proposals Entered</div>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg text-center border border-purple-200">
                    <div className="text-2xl font-bold text-purple-600">{performanceData.ytd_summary.proposals_approved}</div>
                    <div className="text-xs text-purple-700">Proposals Approved</div>
                  </div>
                  <div className="p-3 bg-pink-50 rounded-lg text-center border border-pink-200">
                    <div className="text-2xl font-bold text-pink-600">{performanceData.ytd_summary.book_displays}</div>
                    <div className="text-xs text-pink-700">Book Displays</div>
                  </div>
                  <div className="p-3 bg-teal-50 rounded-lg text-center border border-teal-200">
                    <div className="text-2xl font-bold text-teal-600">{performanceData.ytd_summary.literacy_stations}</div>
                    <div className="text-xs text-teal-700">Literacy Stations</div>
                  </div>
                </div>
              </div>
              
              {/* Period Activity */}
              <div>
                <h4 className="flex items-center gap-2 text-sm font-medium mb-3">
                  <Calendar className="h-4 w-4" />
                  {formatDateMMDDYY(new Date())} Activity
                </h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg text-center border">
                    <div className="text-2xl font-bold">{performanceData.period_activity.total_events}</div>
                    <div className="text-xs text-muted-foreground">Period Events</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center border">
                    <div className="text-2xl font-bold">{performanceData.period_activity.completed}</div>
                    <div className="text-xs text-muted-foreground">Completed</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center border">
                    <div className="text-2xl font-bold">{performanceData.period_activity.open_in_progress}</div>
                    <div className="text-xs text-muted-foreground">Open/In Progress</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center border">
                    <div className="text-2xl font-bold">{performanceData.period_activity.future_events}</div>
                    <div className="text-xs text-muted-foreground">Future Events</div>
                  </div>
                </div>
              </div>
              
              {/* Competency Analysis */}
              {Object.keys(performanceData.competency_analysis || {}).length > 0 && (
                <div>
                  <h4 className="flex items-center gap-2 text-sm font-medium text-purple-700 mb-3">
                    <Award className="h-4 w-4" />
                    Year-to-Date Competency Analysis
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h5 className="text-xs font-medium mb-2">Core Competencies Distribution</h5>
                      <div className="space-y-2">
                        {Object.entries(performanceData.competency_analysis).map(([comp, count]) => (
                          <div key={comp} className="flex items-center justify-between text-sm">
                            <span>{comp}</span>
                            <Badge variant="outline">{count}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Action buttons */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleExportDocx}
                    disabled={!selectedStaffId || reportExportLoading === 'docx'}
                  >
                    {reportExportLoading === 'docx' ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Word (.docx)
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleExportPdf}
                    disabled={!selectedStaffId || reportExportLoading === 'pdf'}
                  >
                    {reportExportLoading === 'pdf' ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    PDF
                  </Button>
                </div>
                <Button variant="outline" onClick={() => setPerformanceDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Competency Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-600" />
              Review Competency Suggestions
            </DialogTitle>
            <DialogDescription>
              Review, edit, and approve or reject the suggested competencies for this event
            </DialogDescription>
          </DialogHeader>
          
          {reviewingEvent && (
            <div className="space-y-6 py-4">
              {/* Event Info */}
              <div className="p-4 bg-stone-50 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-semibold text-lg">{reviewingEvent.title}</h3>
                  <Badge variant="outline" className="capitalize">{reviewingEvent.event_type}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  {reviewingEvent.description?.slice(0, 150)}
                  {reviewingEvent.description?.length > 150 ? "..." : ""}
                </p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    {reviewingEvent.creator_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {reviewingEvent.event_date || reviewingEvent.date}
                  </span>
                </div>
              </div>

              {/* Original Suggestions */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">
                  Staff Suggested:
                </h4>
                <div className="flex flex-wrap gap-2">
                  {reviewingEvent.suggested_competencies?.map((comp) => (
                    <Badge key={comp} className="bg-amber-100 text-amber-700">
                      {comp}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Editable Competencies */}
              <div>
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Edit className="h-4 w-4" />
                  Edit Competencies (select to approve)
                </h4>
                
                {/* Essential Functions */}
                <div className="mb-4">
                  <h5 className="text-xs font-medium text-purple-700 mb-2">I. Essential Functions</h5>
                  <div className="grid grid-cols-2 gap-2">
                    {ESSENTIAL_FUNCTIONS.map((func) => {
                      const isSelected = editedCompetencies.includes(func.value);
                      return (
                        <button
                          key={func.value}
                          type="button"
                          onClick={() => toggleCompetency(func.value)}
                          className={`text-left p-2 rounded-lg border text-sm transition-all ${
                            isSelected 
                              ? "border-purple-500 bg-purple-50 text-purple-800" 
                              : "border-gray-200 hover:border-purple-300"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                              isSelected ? "bg-purple-500 border-purple-500" : "border-gray-300"
                            }`}>
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <span>{func.label}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* LA Competencies */}
                <div>
                  <h5 className="text-xs font-medium text-blue-700 mb-2">II. LA Competencies</h5>
                  <div className="grid grid-cols-2 gap-2">
                    {LA_COMPETENCIES.map((comp) => {
                      const isSelected = editedCompetencies.includes(comp.value);
                      return (
                        <button
                          key={comp.value}
                          type="button"
                          onClick={() => toggleCompetency(comp.value)}
                          className={`text-left p-2 rounded-lg border text-sm transition-all ${
                            isSelected 
                              ? "border-blue-500 bg-blue-50 text-blue-800" 
                              : "border-gray-200 hover:border-blue-300"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                              isSelected ? "bg-blue-500 border-blue-500" : "border-gray-300"
                            }`}>
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <span>{comp.label}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Selected Summary */}
              {editedCompetencies.length > 0 && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <h5 className="text-xs font-medium text-green-800 mb-2">
                    Will be approved ({editedCompetencies.length}):
                  </h5>
                  <div className="flex flex-wrap gap-1">
                    {editedCompetencies.map((comp) => (
                      <Badge key={comp} className="bg-green-100 text-green-700 text-xs">
                        {comp}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Reject Notes */}
              <div>
                <Label className="text-sm">Rejection Notes (optional)</Label>
                <Textarea
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  placeholder="If rejecting, explain why..."
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setReviewDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleCompetencyReject}
              disabled={competencyActionLoading}
            >
              {competencyActionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </>
              )}
            </Button>
            <Button
              onClick={handleCompetencyApprove}
              disabled={competencyActionLoading || editedCompetencies.length === 0}
              className="bg-green-600 hover:bg-green-700"
            >
              {competencyActionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve ({editedCompetencies.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
